import * as React from 'react';
import { History } from 'history';
import { Container, Grid } from '@mui/material';
import { AppBar, ParallaxScroll, Text, Transition, Trapezoid } from '.';
import { align, maxwidth, position, textalign, textsize, transition } from '../services/app.service';

interface Props {
  id?: string;
  appbar?: {
    history?: History,
    color?: any,
    logo?: React.ReactNode,
    logoCaption?: string,
    avatar?: any,
    menu?: any,    
    menuColor?: any;
    width?: number;
    onClick?: () => void;
    onScrollEnd?: () => void;
    onHide?: (data: any) => void;
    children: React.ReactNode;
  };
  section: {
    id?: string,
    seq?: number,
    color?: any,
    align?: align,
    position?: position,
    maxWidth?: maxwidth,
    fullHeight?: boolean,
    hide?: boolean,
    trapezoid?: position,
    transition?: {
      type?: transition,
      time?: number,
      play?: boolean,
    }
    background?: {
      image?: string,
      size?: string,
      position?: string,
      element?: React.ReactNode,
    }
    content?: {
      id?: string,
      seq?: number,
      caption?: {
        id?: string,
        seq?: number,
        text: string, 
        size?: textsize,
        color?: any,
        textAlign?: textalign,
        font?: string,
        bold?: boolean,
        textToHtml?: boolean,
        displayBlock?: boolean;
        icon?: string;
        iconColor?: any;
        iconSize?: textsize;
        lineHeight?: number,
        url?: string,
        marginTop?: number,
        marginBottom?: number,
        onClick?: () => void;
        hide?: boolean,
      }[],
      captionPosition?: position,
      columnSize?: number,
      hide?: boolean,
      child?: React.ReactNode,
    }[],
  }[];
  sx?: any;
}

export const PageSection: React.FunctionComponent<Props> = (props) => {
  const [transitionPlay, setTransitionPlay] = React.useState(true);
  const [sections, setSections] = React.useState<any>();  
  var addTime: number = 0;
  
  const contentCaption = (section: any, content: any) => {
    return (
      <> 
      {content?.caption?.map((d: any, i: number) => {
        addTime += (section.transition?.time || 500); 
        const textAlign = d?.textAlign || section.align || (content.captionPosition === 'left'? 'right' : 'left');
        if (!d?.hide)
        return ( 
          <Text key={i} label={d?.text} size={d?.size || 'extralarge'} color={d?.color} textAlign={textAlign} bold={d?.bold} font={d?.font} textToHtml={d?.textToHtml} displayBlock={d?.displayBlock} icon={d?.icon} iconColor={d?.iconColor} iconSize={d?.iconSize} lineHeight={d?.lineHeight} url={d?.url} marginTop={d?.text && d?.marginTop} marginBottom={d?.text && d?.marginBottom} transition={section.transition?.type} transitionParentid={section.id} transitionTime={addTime} transitionPlay={transitionPlay} onClick={d?.onClick}/>
        ) 
      })} 
      </>
    )
  }

  const contentChild = (section: any, content: any) => {
    const marginTop = ['', 'top'].includes(content.captionPosition) && content.caption?.length > 0 && content.child ? 5 : 0

    return (
      <Transition parentId={props.id} type={section.transition?.type} addTime={addTime += (section.transition?.time || 500)} play={transitionPlay}>
        <Grid textAlign={section.align} mt={marginTop}>{content.child}</Grid>
      </Transition>
    )
  }

  const layoutTopBottom = (section: any, content: any) => {
    return (
      <>
        {(!content.captionPosition || content.captionPosition === 'top') && contentCaption(section, content)}
        {contentChild(section, content)}
        {content.captionPosition === 'bottom' && contentCaption(section, content)}
      </>
    )
  }

  const layoutLeftRight = (section: any, content: any) => {
    return (
      <Grid container direction={content.captionPosition === 'left'? 'row' : 'row-reverse'} columnSpacing={2} alignItems='center' justifyContent='center'>   
        <Grid item xs={12} md={3} textAlign='right' mb={3}>
          {contentCaption(section, content)}
        </Grid>
        <Grid item xs={12} md={9}>
          {contentChild(section, content)}
        </Grid>
      </Grid>
    )
  }

  const child = (section: any) => {
    return (
      <Container maxWidth={section.maxWidth || 'md'} disableGutters >
        <Transition parentId={section.id} type={section.transition} play={transitionPlay}>
          <Grid container alignItems={section.position} height={section.fullHeight? '110vh' : 'auto'} pt={section.type? 5 : 0} pb={section.type? 5 : 0}>
            {section.content?.map((d: any, i:number) => {
              if (!d.hide)
              return(
                <Grid key={i} item xs={12} md={d.columnSize} mt={d.marginTop} mb={d.marginBottom}>
                  {d.captionPosition === 'left' || d.captionPosition === 'right'? layoutLeftRight(section, d) : layoutTopBottom(section, d)}
                </Grid>
              )
            })}
          </Grid>
        </Transition>
      </Container>
    )
  }

  const sectionUpdate = () => {
    //d0: prev data, d1: next data
    var d0: any;
    var section = props.section;
    
    return section?.filter((d: any) => d && !d.hide)
      .map((d: any, i: number, d1: any) => {
        if (d.trapezoid?.includes('top')) d.colorOther = d0?.color;
        else if (d.trapezoid?.includes('bottom')) d.colorOther = d1[i+1]?.color;
        d0 = d;
        return d; 
      });
  }

  const sectionLoad = () => {
    return (
      sections?.map((d: any, i: number) => {
        addTime = 0;
        return (
          <Trapezoid key={i} id={d.id} position={d.trapezoid} color={d.color} colorOther={d.colorOther}>
            {d.background? 
            <ParallaxScroll background={d.background} backgroundElement={d.background?.element}> {child(d)} </ParallaxScroll>
            : child(d)
            }
          </Trapezoid>
        )
      })
    )
  }

  const sectionLoadAppbar = () => {
    return (
      <AppBar {...props.appbar}
        color= {props.appbar?.color || 'background.default'}
        onClick= {onClickAppBar}
        onScrollEnd = {onScrollEndAppBar}
      >
        {sectionLoad()}
      </AppBar>
    )
  }

  const onClickAppBar = () => {
    setTimeout(() => setTransitionPlay(true), 100);
  }

  const onScrollEndAppBar = () => {
    setTransitionPlay(false);
  }

  React.useEffect(() => {
    setSections(sectionUpdate());

  }, [props.section])

  return (
    <Grid id={props.id} minHeight='100vh' bgcolor='background.default' sx={props.sx}> 
      {props.appbar? sectionLoadAppbar() : sectionLoad()} 
    </Grid>
  )
  
}